/**
 * SMTP Settings admin page
 */
import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { parseServerDate } from '../../utils/formatDateTime'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import { getSmtpSettings, updateSmtpSettings, testSmtpSettings } from '../../services/admin'
import type { SmtpSettings } from '../../types'

type TlsMode = 'none' | 'STARTTLS' | 'TLS'

function tlsModeFromSettings(s: SmtpSettings): TlsMode {
  if (s.use_tls) return 'TLS'
  if (s.use_starttls) return 'STARTTLS'
  return 'none'
}

const PASSWORD_MASK = '••••••'

export default function SmtpSettingsPage() {
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [showTestInput, setShowTestInput] = useState(false)
  const [testRecipient, setTestRecipient] = useState('')

  // Form state
  const [host, setHost] = useState('')
  const [port, setPort] = useState(587)
  const [fromName, setFromName] = useState('FCC License Manager')
  const [fromEmail, setFromEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [tlsMode, setTlsMode] = useState<TlsMode>('STARTTLS')
  const [enabled, setEnabled] = useState(false)
  const [lastTestAt, setLastTestAt] = useState<string | null>(null)
  const [lastTestResult, setLastTestResult] = useState<string | null>(null)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      const s = await getSmtpSettings()
      setHost(s.host)
      setPort(s.port)
      setFromName(s.from_name)
      setFromEmail(s.from_email)
      setUsername(s.username ?? '')
      setPassword(s.password ?? '') // "••••••" if saved, null if not
      setTlsMode(tlsModeFromSettings(s))
      setEnabled(s.enabled)
      setLastTestAt(s.last_test_at ?? null)
      setLastTestResult(s.last_test_result ?? null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load SMTP settings')
    } finally {
      setLoading(false)
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaveError('')
    setSaveSuccess(false)
    setTestResult(null)
    setSaving(true)
    try {
      const payload: Partial<SmtpSettings> = {
        host,
        port,
        from_name: fromName,
        from_email: fromEmail,
        username: username || null,
        password: password || null,
        use_tls: tlsMode === 'TLS',
        use_starttls: tlsMode === 'STARTTLS',
        enabled,
      }
      const updated = await updateSmtpSettings(payload)
      setLastTestAt(updated.last_test_at ?? null)
      setLastTestResult(updated.last_test_result ?? null)
      // If password was saved, backend returns the mask
      if (updated.password) setPassword(updated.password)
      setSaveSuccess(true)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  async function handleTest(recipient: string) {
    setTesting(true)
    setTestResult(null)
    setSaveError('')
    setSaveSuccess(false)
    try {
      const result = await testSmtpSettings(recipient)
      setTestResult(result)
      setShowTestInput(false)
      // Refresh to get updated last_test_at / last_test_result
      await load()
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Test failed',
      })
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Delivery</h2>
        <div className="text-gray-500">Loading…</div>
      </div>
    )
  }

  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Delivery</h2>

      {loadError && <div className="alert alert-error mb-6">{loadError}</div>}

      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Email Server Configuration</h2>

        {saveError && <div className="alert alert-error mb-4">{saveError}</div>}
        {saveSuccess && (
          <div className="alert alert-success mb-4">Settings saved successfully.</div>
        )}
        {testResult && (
          <div className={`alert ${testResult.success ? 'alert-success' : 'alert-error'} mb-4`}>
            Test {testResult.success ? 'succeeded' : 'failed'}: {testResult.message}
          </div>
        )}

        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="label">Host</label>
              <input
                type="text"
                className="input"
                required
                placeholder="smtp.example.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Port</label>
              <input
                type="number"
                className="input"
                required
                min={1}
                max={65535}
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value, 10) || 587)}
              />
            </div>
            <div>
              <label className="label">From Name</label>
              <input
                type="text"
                className="input"
                required
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
              />
            </div>
            <div>
              <label className="label">From Email</label>
              <input
                type="email"
                className="input"
                required
                placeholder="noreply@example.com"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Username</label>
              <input
                type="text"
                className="input"
                placeholder="(optional)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Password</label>
              <input
                type="password"
                className="input"
                placeholder={password === PASSWORD_MASK ? '(saved — enter new to change)' : ''}
                value={password === PASSWORD_MASK ? '' : password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => {
                  if (password === PASSWORD_MASK) setPassword('')
                }}
                onBlur={() => {
                  // If user cleared the field without typing, restore mask
                  if (password === '') setPassword(PASSWORD_MASK)
                }}
              />
              {password === PASSWORD_MASK && (
                <p className="text-xs text-gray-400 mt-0.5">Password is saved. Click to change.</p>
              )}
            </div>
            <div>
              <label className="label">TLS Mode</label>
              <select
                className="input"
                value={tlsMode}
                onChange={(e) => setTlsMode(e.target.value as TlsMode)}
              >
                <option value="none">None (plaintext)</option>
                <option value="STARTTLS">STARTTLS (port 587)</option>
                <option value="TLS">TLS/SSL (port 465)</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="enabled"
                className="h-6 w-6"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                Enable email sending
              </label>
            </div>
          </div>

          {/* Last test info */}
          {lastTestAt && (
            <p className="text-xs text-gray-500 mb-4">
              Last test: {format(parseServerDate(lastTestAt)!, 'MM/dd/yyyy HH:mm')} —{' '}
              <span
                className={
                  lastTestResult?.toLowerCase().includes('success')
                    ? 'text-green-600'
                    : 'text-red-600'
                }
              >
                {lastTestResult}
              </span>
            </p>
          )}

          <div className="flex flex-col gap-3">
            <div className="flex gap-3 flex-wrap">
              <button type="submit" disabled={saving} className="btn btn-primary">
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
              {!showTestInput && (
                <button
                  type="button"
                  disabled={testing}
                  onClick={() => {
                    setTestRecipient(user?.email ?? '')
                    setShowTestInput(true)
                    setTestResult(null)
                  }}
                  className="btn btn-secondary"
                >
                  Send Test Email
                </button>
              )}
            </div>

            {showTestInput && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <label className="text-sm text-gray-700 whitespace-nowrap">Send to:</label>
                <input
                  type="email"
                  className="input flex-1"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleTest(testRecipient) }
                    if (e.key === 'Escape') setShowTestInput(false)
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={testing || !testRecipient.trim()}
                  onClick={() => handleTest(testRecipient)}
                >
                  {testing ? 'Sending…' : 'Send'}
                </button>
                <button
                  type="button"
                  className="text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => setShowTestInput(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
